// Copyright 2021-2024, 2026 - Hewlett Packard Enterprise Company

import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useFlags from '../hooks/useFlags';
import { AuthConsumer } from '../contexts/AuthContext';
import {
  isDevEnvAuth,
  postLoginRedirect,
  redirectToGlcpLogin,
  saveRedirectRoute,
} from '../utils/authHelpers';
import { WORKSPACE_ID_KEY } from '../constants/authConst';

const GLCP_QUERY_PARAMS = [
  'cid',
  'workspace_id',
  'account_type',
  'service_offer',
];

export default function Login() {
  const history = useHistory();
  const location = useLocation();
  const flags = useFlags();
  const preselectWorkspaceFlag =
    flags.T_20260219_122476_INDIGO_PRESELECT_SAME_WORKSPACE_BOOL();
  const deepLinkFlag = flags.T_20260513_122476_WORKSPACE_DEEP_LINK_BOOL();

  return (
    <AuthConsumer>
      {({ getUser, login, getCid, getWorkspaceId, updateTenantAcid }) => {
        async function doLogin() {
          const params = new URLSearchParams(location.search);

          const loginParams = new Map();
          // parse out GLCP specific query params from login flow
          GLCP_QUERY_PARAMS.forEach(param => {
            if (params.has(param)) {
              loginParams.set(param, params.get(param));
              params.delete(param);
            }
          });

          // if the user was redirected to login from a different route, preserve the route info
          if (window.location.pathname !== '/login') {
            saveRedirectRoute(window.location.pathname, params.toString());
          }

          const cid = loginParams.get('cid');
          const workspaceId = loginParams.get('workspace_id');

          // clear tenant acid on /login to prevent stale data
          updateTenantAcid(null);

          const user = await getUser();
          if (user) {
            // use case: user navigated back to GLCP and then launched COM again
            const cidChanged = cid && cid !== (await getCid());
            const workspaceChanged =
              workspaceId && workspaceId !== (await getWorkspaceId());
            if (cid && (cidChanged || workspaceChanged)) {
              // use case: user switched workspaces while in GLCP
              await login({ cid, workspaceId });
            } else if (!cid && (cidChanged || workspaceChanged)) {
              // error case: workspace_id arrived without cid — redirect
              // through GLCP to obtain a valid cid rather than corrupting
              // auth state
              // eslint-disable-next-line no-console
              console.warn(
                'Login: workspace_id provided without cid, ' +
                  'redirecting through GLCP login',
              );
              redirectToGlcpLogin({ workspaceId, deepLinkFlag });
            } else {
              // user is already authenticated, skip login flow
              postLoginRedirect(history);
            }
            return;
          }

          // use case: GLCP cross-launch — cid and workspace_id provided in params
          if (cid) {
            await login({ cid, workspaceId });
            return;
          }

          // use case: dev environment — fake cid for local development.
          // We are not pointing at a GLP auth server, so we use a hard-coded cid.
          if (isDevEnvAuth()) {
            await login({ cid: 'aci1', workspaceId });
            return;
          }

          // use case: open in new tab/window — read workspace from localStorage
          let storedWorkspaceId;
          if (preselectWorkspaceFlag) {
            storedWorkspaceId =
              localStorage.getItem(WORKSPACE_ID_KEY) || undefined;
          }

          // use case: unauthenticated with no GLCP params — redirect to GLCP login.
          // The workspace_id hint is included only when
          // T_20260513_122476_WORKSPACE_DEEP_LINK_BOOL is on.
          redirectToGlcpLogin({
            workspaceId: storedWorkspaceId,
            deepLinkFlag,
          });
        }

        doLogin();
      }}
    </AuthConsumer>
  );
}
